<template>
  <div class="w-100 my-5">
    <div v-if="eventList">
      <div class="row mx-auto box-white">
        <div class="col-sm my-1 vertical-center" v-if="userInfo">
          <img class="circle-image-30" :src="userInfo.photoUrl ? userInfo.photoUrl : assets.profile" />
          <span class="ml-2 font-15 font-bold">{{ userInfo.userName }}</span>
          <span class="ml-5 font-bold font-15 alpha-07">{{ $t('event') }}:</span>
          <span class="mx-5 font-bold font-20">{{ items.length }}</span>
        </div>
        <div class="col-sm my-1 vertical-center" v-else>
          <span class="font-bold font-15 alpha-07">{{ $t('event') }}:</span>
          <span class="mx-5 font-bold font-20">{{ items.length }}</span>
        </div>
        <div :class="`col-sm my-1 vertical-center ${isMobile ? '' : 'justify-content-end'}`">
          <input class="w-75" v-model="keyword" />
          <v-btn class="ml-2" icon small @click="keyword = ''" :disabled="!keyword">
            <i class="flaticon-circle"></i>
          </v-btn>
        </div>
      </div>
      <div class="row my-5" v-if="(items.length > 0)">
        <div :class="`col-sm${colSmSize}`" v-for="(item, index) in items" :key="(item, index)">
          <v-card class="my-2 p-0 box-white d-flex flex-column">
            <v-img style="cursor: zoom-in" height="200px" :src="item.photoUrl" @click="openImageViewer(index)" />
            <div class="m-2 vertical-center" v-if="!userInfo">
              <img class="circle-image-30" :src="item.userPhotoUrl" />
              <span class="ml-2 ellipsis font-12">{{ item.userName }}</span>
            </div>
            <div class="mx-3 mt-1 vertical-center">
              <i class="fa fa-calendar-alt font-12 color-black"></i>
              <span class="ml-2 font-12 color-black">{{ getDateStringFromTimestamp(item.startAt) }} - {{ getDateStringFromTimestamp(item.endAt) }}</span>
            </div>
            <span class="mx-3 mt-1 ellipsis font-bold font-15">{{ item.name }}</span>
            <span class="mx-3 mt-2 ellipsis font-12">{{ item.description }}</span>
            <div class="mx-3 mt-2 vertical-center">
              <i class="flaticon-placeholder-2 font-12 color-black"></i>
              <span class="ml-2 ellipsis font-12 color-black">{{ item.address }}</span>
              <span class="ml-2 font-12 color-black" v-if="item.hideAddress">{{ $t('private') }}</span>
            </div>
            <div class="mx-3 mt-2 vertical-center justify-content-between">
              <div class="my-2 vertical-center ">
                <i class="fa fa-calendar-alt font-12 color-black"></i>
                <span class="ml-2 font-12">{{ getDateStringFromTimestamp(item.createdAt) }}</span>
              </div>
              <div class="vertical-center">
                <template v-if="item.deletedAt">
                  <i class="fa fa-calendar-alt font-12 color-black"></i>
                  <span class="ml-2 font-12 color-black">{{ getDateStringFromTimestamp(item.deletedAt) }}</span>
                  &nbsp;
                  <v-btn class="mr-auto" icon small @click="$toast.info(item.deletedReason)" v-if="item.deletedReason">
                    <i class="flaticon-info color-black"></i>
                  </v-btn>
                  <i class="font-12 color-black" v-else>[{{ $t('deleted') }}]</i>
                </template>
                <template v-else>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on">
                        <i class="fa fa-info-circle color-black"></i>
                      </v-btn>
                    </template>

                    <div class="pa-2">
                      <div v-for="cat in item.tickets" :key="cat.id" class="mb-1 ticket-tooltip-content">
                        <strong>{{ cat.name }}</strong> : {{ cat.soldQuantity || 0 }} / {{ cat.totalQuantity }}
                      </div>
                    </div>
                  </v-tooltip>

                  <v-btn icon small @click="setEventStatus(item.eventId, item.sellingStatus === 'selling_very_quickly' ? null : 'selling_very_quickly', 'selling')">
                    <i v-if="item.sellingStatus === 'selling_very_quickly'" class="fa fa-fire" style="color:black"></i>
                    <i v-else class="fa fa-fire"></i>
                  </v-btn>

                  <v-btn icon small @click="setEventStatus(item.eventId, item.soldStatus === 'almost_sold_out' ? null : 'almost_sold_out', 'sold')">
                    <i v-if="item.soldStatus === 'almost_sold_out'" class="fa fa-hourglass-half" style="color:black"></i>
                    <i v-else class="fa fa-hourglass-half"></i>
                  </v-btn>

                  <v-btn icon small @click="deleteEventInfoTask(item.eventId, item.userId)">
                    <i class="fa fa-trash color-black"></i>
                  </v-btn>
                </template>
              </div>
            </div>
            <div class="m-2 d-flex justify-content-start">
              <v-btn class="ml-5 button-normal" small @click="$router.push(`/event/${item.eventId}/coupons`)" v-if="!item.deletedAt && isFutureTimestamp(item.endAt)">
                <span>{{ $t('add_coupon') }}</span>
              </v-btn>
              <v-btn class="ml-5 button-normal" small @click="$router.push(`/transaction/transactions/${item.eventId}`)">
                <span>{{ $t('transaction') }}</span>
              </v-btn>
            </div>
            <div class="m-2 mb-3 d-flex justify-content-start vertical-center flex-wrap" style="gap: 12px;">
              <v-btn 
                class="ml-5 button-normal"
                small 
                @click="setEventHighlight(item.eventId, !item.isHighlighted)"
                v-if="!item.deletedAt && isFutureTimestamp(item.endAt)"
              >
                <span>{{ $t(item.isHighlighted ? 'remove_highlight' : 'set_highlight') }}</span>
              </v-btn>
              <v-btn 
                class="ml-5 button-normal"
                small 
                @click="exportBuyers(item.eventId, item.name)"
                v-if="item.soldQuantity > 0"
              >
                <span>{{ $t('export_buyers') }}</span>
              </v-btn>
            </div>    
            <div class="mx-2 mb-4 d-flex justify-content-start vertical-center">
              <v-btn class="ml-5 button-normal" small @click="$router.push(`/transaction/refund/${item.eventId}`)">
                <span>{{ $t('refund') }}</span>
              </v-btn>
              <v-btn class="ml-5 button-normal" small @click="$router.push(`/transaction/withdraws/${item.eventId}`)">
                <span>{{ $t('withdraw') }}</span>
              </v-btn>
          </div>
          </v-card>
        </div>
      </div>
      <div class="py-40 center" v-else>
        <span class="font-20 alpha-07">{{ $t('no_data_found') }}</span>
      </div>
    </div>
    <div class="py-40 center" v-else>
      <p>{{ $t('please_wait') }}</p>
      <b-spinner type="grow"></b-spinner>
    </div>
    <!-- VueEasyLightbox Component -->
    <VueEasyLightbox
      :visible="visibleImageViewer"
      :imgs="images"
      :index="imageIndex"
      @hide="visibleImageViewer = false"
      />
    <!-- VueEasyLightbox Component end -->
  </div>
</template>


<script>
import VueEasyLightbox from 'vue-easy-lightbox';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../main';
import { getDateStringFromTimestamp, showLoading, isFutureTimestamp } from '../../../functions';

import profile from '@/assets/image/profile.jpg';

export default {
  name: 'EventList',
  components: {
    VueEasyLightbox
  },
  computed: {
    isMobile() {
      return this.$store.state.isMobile;
    },
    colSmSize() {
      return this.$store.state.colSmSize;
    },
    userList() {
      return this.$store.state.userList;
    },
    userInfo() {
      if (this.$route.params.userId) {
        return this.userList ? this.userList.find(element => element.userId === this.$route.params.userId) : null;
      } else {
        return null;
      }
    },
    eventList() {
      return this.$store.state.eventList;
    },
    transactionList() {
      return this.$store.state.transactionList;
    },
    ticketList() {
      return this.$store.state.ticketList;
    },
    params() {
      return this.$route.params;
    }
  },
  watch: {
    params() {
      if (this.keyword) {
        this.keyword = '';
      } else {
        this.refreshData();
      }
    },
    userList() {
      this.refreshData();
    },
    eventList() {
      this.refreshData();
    },
    ticketList() {
      this.refreshData();
    },
    keyword() {
      this.refreshData();
    }
  },
  data() {
    return {
      assets: {
        profile
      },
      visibleImageViewer: false,
      imageIndex: 0,
      images: [],
      items: [],
      keyword: ''
    }
  },
  mounted() {
    this.refreshData();
  },
  methods: {
    getDateStringFromTimestamp,
    isFutureTimestamp,
    refreshData() {
      if (this.eventList && this.userList) {
        const items = [];
        const keyword = this.keyword.toLowerCase();
        if (this.$route.params.userId) {
          this.eventList.filter(element => element.userId === this.$route.params.userId).forEach(eventInfo => {
            if (eventInfo.name.toLowerCase().includes(keyword)) {
              const item = JSON.parse(JSON.stringify(eventInfo));
              // Add tickets for this event
              if (this.ticketList) {
                item['tickets'] = this.ticketList.filter(ticket => ticket.eventId === eventInfo.eventId);
              } else {
                item['tickets'] = [];
              }
              items.push(item);
            }
          });
        } else {
          this.eventList.forEach(eventInfo => {
            const userInfo = this.getUserInfo(eventInfo.userId);
            if (userInfo && (eventInfo.name.toLowerCase().includes(keyword) || userInfo.userName.toLowerCase().includes(keyword))) {
              const item = JSON.parse(JSON.stringify(eventInfo));
              item['userName'] = userInfo.userName;
              item['userPhotoUrl'] = userInfo.photoUrl ? userInfo.photoUrl : this.assets.profile;
              // Add tickets for this event
              if (this.ticketList) {
                item['tickets'] = this.ticketList.filter(ticket => ticket.eventId === eventInfo.eventId);
              } else {
                item['tickets'] = [];
              }
              items.push(item);
            }
          });
        }
        items.sort((a, b) => (a.createdAt.seconds < b.createdAt.seconds) ? 1 : -1);
        this.images = [];
        items.forEach(info => {
          this.images.push({
            title: info.name,
            src: info.photoUrl
          });
        });
        this.items = items;
      }
    },
    getUserInfo(userId) {
      return this.userList ? this.userList.find(element => element.userId === userId) : null;
    },
    openImageViewer(index) {
      this.imageIndex = index;
      this.visibleImageViewer = true;
    },
    setEventHighlight(eventId, isHighlighted) {
      const params = {
        functionName: 'setHighlight',
        eventId: eventId,
        isHighlighted: isHighlighted
      };
      const loader = showLoading(this, 1);
      const func = httpsCallable(functions, this.ADMIN_FUNCTION);
      func(JSON.stringify(params)).then(response => {
        loader.hide();
        if (response.data === this.RESULT_SUCCESS) {
          this.$toast.success(this.$t('success'));
        } else {
          this.$toast.error(response.data);
        }
      }).catch(error => {
        loader.hide();
        this.$toast.error(error.code + ', ' + error.message);
      });
    },
    exportBuyers(eventId, eventName) {
      const buyers = []
      if (!this.transactionList) return;
      this.transactionList.filter(ele => ele.eventId === eventId).forEach(transactionInfo => {
        const buyerInfo = this.getUserInfo(transactionInfo.buyerId);
        if (buyerInfo) {
          buyers.push(buyerInfo)
        }
      });
      if (buyers.length) {
        // Export as CSV
        // Define the CSV headers
        const headers = ['Name,Email,Phone'];

        // Map the data to CSV rows
        const rows = buyers.map(item => `${item.userName},${item.email || ""},${item.phone || ""}`);

        // Combine headers and rows
        const csvContent = [...headers, ...rows].join('\n');

        // Create a Blob with CSV content
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

        // Create a temporary link and trigger download
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${eventName}_buyers.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link); // Clean up
        URL.revokeObjectURL(url); // Free up memory
      }
    },
    setEventStatus(eventId, status, option) {
      const params = {
        functionName: 'setEventStatus',
        eventId: eventId,
        status: status,
        option: option,
      };
      const loader = showLoading(this, 1);
      const func = httpsCallable(functions, this.ADMIN_FUNCTION);
      func(JSON.stringify(params)).then(response => {
        loader.hide();
        if (response.data === this.RESULT_SUCCESS) {
          this.$toast.success(this.$t('success'));
        } else {
          this.$toast.error(response.data);
        }
      }).catch(error => {
        loader.hide();
        this.$toast.error(error.code + ', ' + error.message);
      });
    },
    deleteEventInfoTask(eventId, userId) {
      if (!confirm(this.$t('confirm_delete_', [this.$t('event')]))) {
        return;
      }
      const reason = prompt(this.$t('reason'));
      if (!reason) {
        return;
      }
      const params = {
        functionName: 'deleteEvent',
        eventId: eventId,
        userId: userId,
        reason: reason
      };
      const loader = showLoading(this, 1);
      const func = httpsCallable(functions, this.ADMIN_FUNCTION);
      func(JSON.stringify(params)).then(response => {
        loader.hide();
        if (response.data === this.RESULT_SUCCESS) {
          this.$toast.success(this.$t('success'));
        } else {
          this.$toast.error(response.data);
        }
      }).catch(error => {
        loader.hide();
        this.$toast.error(error.code + ', ' + error.message);
      });
    }
  }
};
</script>